import React, { useEffect, useRef, useState } from 'react';

//Third Party
import PropTypes from 'prop-types';
import cookie from 'react-cookie';
import Recaptcha from 'react-google-invisible-recaptcha';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, HelpBlock } from 'react-bootstrap';

// Twine Components
import ButtonWithLoader from '../ButtonWithLoader/ButtonWithLoader';

// Redux
import {
  resetEmailError,
  validateEmail,
  signUpPasswordless,
} from '../../redux/modules/auth';
import { trackEvent } from '../../helpers/Analytics';

// Styles
import styles from './BriefDetailsSignUp.module.scss';

const BriefDetailsOnboarding = ({ text, signUpContainerClass }) => {
  const [email, setEmail] = useState('');
  const [signUpDetails, setSignUpDetails] = useState({});
  const [disabledState, setDisabledState] = useState(false);
  const [emailWarningError, setEmailWarningError] = useState('');
  const [emailDangerError, setEmailDangerError] = useState('');
  const [emailInputDanger, setEmailInputDanger] = useState(false);
  const [success, setSuccess] = useState(false);

  const selectAuth = (state) => state.auth;
  const selectEmailError = (state) => state.auth.emailError;
  const authState = useSelector(selectAuth);
  const emailError = useSelector(selectEmailError);

  const {
    emailValidationError,
    emailValidationType,
    signUpPasswordlessLoading,
    signUpPasswordlessResponse,
    signUpPasswordlessError,
  } = authState;

  const recaptchaRef = useRef(null);
  const signUpDetailsRef = useRef(signUpDetails);

  const dispatch = useDispatch();

  const handleEmailChange = (event) => {
    const newEmail = event.target.value;
    setEmail(newEmail);
    setEmailWarningError('');
    setEmailDangerError('');
    setEmailInputDanger(false);
    dispatch(resetEmailError());
  };

  useEffect(() => {
    setDisabledState(signUpPasswordlessLoading);
  }, [signUpPasswordlessLoading]);

  // Existing user error
  useEffect(() => {
    if (emailError.length > 0) {
      setEmailDangerError(emailError[0]?.toString());
    }
  }, [emailError]);

  useEffect(() => {
    if (emailValidationType === 'Invalid') {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailDangerError(emailValidationError);
    } else {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailWarningError(emailValidationError);
    }
  }, [emailValidationError, emailValidationType]);

  useEffect(() => {
    if (signUpPasswordlessResponse && !signUpPasswordlessError) {
      setSuccess(true);
    }
  }, [signUpPasswordlessResponse, signUpPasswordlessError]);

  // Handle signUpPasswordless errors
  useEffect(() => {
    if (signUpPasswordlessError) {
      trackEvent('job-details-sign-up-error', {
        error: signUpPasswordlessError,
      });
      if (signUpPasswordlessError.message) {
        setEmailDangerError(signUpPasswordlessError.message);
      } else if (typeof signUpPasswordlessError === 'string') {
        setEmailDangerError(signUpPasswordlessError);
      } else {
        setEmailDangerError(
          'An error occurred during sign up. Please try again.'
        );
      }
      setEmailInputDanger(true);
    }
  }, [signUpPasswordlessError]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setEmailWarningError('');
      setEmailDangerError('');
      dispatch(resetEmailError());

      if (email !== '') {
        if (email.includes('@') && email.includes('.')) {
          dispatch(validateEmail(email));
        }
      }
    }, 250);

    return () => clearTimeout(delayDebounceFn);
  }, [email]);

  const validateEmailFormat = () => {
    if (email === '') {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailDangerError('Please enter your email address');
      setEmailInputDanger(true);
      return false;
    }

    if (!email.includes('@') || !email.includes('.')) {
      setEmailWarningError('');
      setEmailDangerError('');
      setEmailDangerError("Email doesn't contain @ or .");
      setEmailInputDanger(true);
      return false;
    }

    if (emailDangerError) {
      setEmailInputDanger(true);
      return false;
    }

    return true;
  };

  const handleCreateAccount = () => {
    trackEvent('job-details-sign-up-attempt', { email });
    const isValidEmail = validateEmailFormat();

    if (
      !emailDangerError &&
      isValidEmail &&
      emailValidationType !== 'Invalid'
    ) {
      if (!email) {
        return;
      }

      const generatedSignUpDetails = {
        email: email,
        type: 'seller',
        utm: {},
        ai_collector: false,
      };

      // Save brief-link cookie
      const expires = new Date();
      // set to expire in 5 minutes
      expires.setTime(expires.getTime() + 5 * 60 * 1000);
      cookie.save('brief-link', window.location.pathname, {
        path: '/',
        expires,
      });

      const utmSource = cookie.load('utm_source');
      const utmMedium = cookie.load('utm_medium');
      const utmCampaign = cookie.load('utm_campaign');
      const utmTerm = cookie.load('utm_term');
      const utmContent = cookie.load('utm_content');
      const briefLink = window.location.pathname;

      if (utmSource) {
        generatedSignUpDetails.utm.source = utmSource;
      }

      if (utmMedium) {
        generatedSignUpDetails.utm.medium = utmMedium;
      }

      if (utmCampaign) {
        generatedSignUpDetails.utm.campaign = utmCampaign;
      }

      if (utmTerm) {
        generatedSignUpDetails.utm.term = utmTerm;
      }

      if (utmContent) {
        generatedSignUpDetails.utm.content = utmContent;
      } else if (briefLink) {
        generatedSignUpDetails.utm.content = briefLink;
      }

      if (recaptchaRef.current) {
        setSignUpDetails(generatedSignUpDetails);
      } else {
        dispatch(signUpPasswordless(generatedSignUpDetails)).then(() => {
          trackEvent('job-details-sign-up-success', { email });
          redirectToAuthCode(btoa(email));
        });
      }
    }
  };

  useEffect(() => {
    signUpDetailsRef.current = signUpDetails;
    if (Object.keys(signUpDetails).length > 0) {
      recaptchaRef.current.execute();
    }
  }, [signUpDetails]);

  const handleRecaptchaResolved = () => {
    //had issues trying to access the signUpDetails state here
    //chatGPT suggested using a Ref instead and it worked
    signUpDetailsRef.current.recaptcha = recaptchaRef.current.getResponse();
    dispatch(signUpPasswordless(signUpDetailsRef.current)).then(() => {
      trackEvent('job-details-sign-up-success', {
        email: signUpDetailsRef.current.email,
      });
      redirectToAuthCode(btoa(signUpDetailsRef.current.email));
    });
    recaptchaRef.current.reset();
  };

  const redirectToAuthCode = (encodedEmail) => {
    window.location = `/auth/code?email=${encodedEmail}`;
  };

  return (
    <>
      <div className={signUpContainerClass}>
        <div className={styles.inputContainer}>
          <FormControl
            type="email"
            placeholder="e.g. name@company.com"
            disabled={false}
            value={email}
            onChange={(event) => handleEmailChange(event)}
            className={[styles.input, emailInputDanger && styles.inputDanger]}
          />
        </div>
        <ButtonWithLoader
          bsStyle="primary"
          disabled={disabledState}
          style={{ minWidth: 75 }}
          onClick={() => handleCreateAccount()}
          loading={disabledState}
          text={text}
          success={success}
        />
      </div>
      {!emailWarningError && !emailDangerError && <HelpBlock>&nbsp;</HelpBlock>}
      {emailWarningError && (
        <HelpBlock className={styles.emailWarning}>
          {emailWarningError}
        </HelpBlock>
      )}
      {emailDangerError && (
        <HelpBlock className={styles.emailDanger}>{emailDangerError}</HelpBlock>
      )}
      <p className={styles.termsText}>
        By applying, you agree to our <a href="/terms">Terms</a>.
      </p>
      {process.env.GOOGLE_RECAPTCHA_API_KEY && (
        <Recaptcha
          ref={recaptchaRef}
          sitekey={process.env.GOOGLE_RECAPTCHA_API_KEY}
          onResolved={handleRecaptchaResolved}
        />
      )}
    </>
  );
};

BriefDetailsOnboarding.propTypes = {
  text: PropTypes.string,
  signUpContainerClass: PropTypes.string,
};

export default BriefDetailsOnboarding;
